import React, {useEffect, useMemo, useState} from 'react';
import {Box, Button, Typography} from '@mui/material';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {
  OVRecommendActionEnum,
  GlobalPermissionEnum,
} from '../../../schema-types/globalTypes';
import AutoField, {ActionType} from '../../../components/AutoForm/AutoFieldNew';
import {BuilderMandatesWrapper as MandatesWrapper} from '../pages/BuilderMandatesModal';
import {FormReadOnly} from '../../../components/AutoForm/AutoFormNew';
import {PotDetails} from '../../../schema-types/PotDetails';
import {useDjangoContext} from '../../../contexts/useAuthContext';
import {useAutoField} from '../../../components/AutoForm/useAutoForm';
import {ScrollableModal} from '../../../components/Common/ScrollableModal';
import {BuilderPlanFeesField, planFeesFieldType} from '../CreateBuilder/BuilderPlanFees';
import {UPDATES} from '../../../shared/UPDATES';

export const BuilderModifyFields: React.FC<{sourcePot: PotDetails | undefined}> = ({
  sourcePot,
}) => {
  const {perms} = useDjangoContext();
  const [_, modifyActionMeta, __] = useAutoField('modify.action');
  const [modifyFieldObj, modifyMeta, modifyHelpers] =
    useAutoField<builderFieldsType['modify']>('modify');
  const [planFeesFieldObj, planFeesMeta, planFeesHelpers] =
    useAutoField<planFeesFieldType | null>('modify.planFees');
  const [showContributeWithdrawalWarning, setContributeWithdrawalWarning] =
    useState(false);
  // IMPORTANT: the line below used to be
  // const modalInner = accountIsSpp ? (
  const modalInner = perms.includes(GlobalPermissionEnum.can_access_ehons_notes_goals) ? (
    <>
      <h2>⚠️ Important</h2>
      <p>
        You cannot amend or cancel an existing regular withdrawal or contribution using a{' '}
        <em>Modify Requirement</em>.
      </p>
      <ul>
        <li>
          To <strong>change</strong> an existing regular, create a new{' '}
          <em>Contribution/Withdrawal Requirement</em> with the revised amount/frequency
          and use the <em>Effective Date </em> to set when it should apply. Add an ad-hoc
          task to confirm which regular this replaces.
        </li>
        <li>
          To <strong>stop</strong> a regular, add an ad-hoc task with the details and the
          date the change should take effect.
        </li>
      </ul>
    </>
  ) : (
    <>
      <Typography color="primary" variant="h6">
        Please add a new instruction for the updated contribution/withdrawal details and
        submit.
      </Typography>
      <Box mt={2} />
      <Typography color="primary" variant="body1">
        Once submitted, please confirm the details of the existing contribution/withdrawal
        that need to be stopped. Please use the Modify Notes box to provide as much
        information as possible including the date the change should take effect.
      </Typography>
    </>
  );

  const modelChange = useMemo(() => {
    // if not loaded yet, dont check yet
    if (!sourcePot || !modifyFieldObj.value?.mandateChange) {
      return false;
    }
    if (modifyFieldObj.value?.mandateChange) {
      if (
        modifyFieldObj.value?.model &&
        typeof modifyFieldObj.value?.model !== 'string'
      ) {
        if (
          modifyFieldObj.value.model.providerContactLastName &&
          sourcePot?.modelPortfolio?.providerContact?.lastName !==
            modifyFieldObj.value.model.providerContactLastName
        ) {
          return true;
        }
        if (
          modifyFieldObj.value.model.providerContactJobTitle &&
          sourcePot?.modelPortfolio?.providerContact?.jobTitle !==
            modifyFieldObj.value.model.providerContactJobTitle
        ) {
          return true;
        }
      }
    }
    return false;
  }, [
    modifyFieldObj.value?.mandateChange,
    sourcePot?.modelPortfolio,
    modifyFieldObj.value?.model,
  ]);

  useEffect(() => {
    if (!modelChange) {
      planFeesHelpers.setValue(UPDATES._D663, null);
    }
  }, [modelChange]);

  useEffect(() => {
    if (
      modifyFieldObj.value?.regularContributionChange ||
      modifyFieldObj.value?.regularWithdrawalChange
    ) {
      setContributeWithdrawalWarning(true);
    }
  }, [
    modifyFieldObj.value?.regularContributionChange,
    modifyFieldObj.value?.regularWithdrawalChange,
  ]);

  return (
    <Box boxShadow={2} borderRadius="15px" bgcolor="white" padding={2}>
      <ScrollableModal
        sx={{
          margin: '5% 15% 0 15%',
        }}
        open={showContributeWithdrawalWarning}
        size="medium"
        footer={
          <Box display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setContributeWithdrawalWarning(false);
              }}
            >
              Ok
            </Button>
          </Box>
        }
      >
        <Box
          bgcolor="white"
          display="flex"
          flex={1}
          flexDirection="column"
          justifyContent="space-between"
          padding={1}
        >
          {modalInner}
        </Box>
      </ScrollableModal>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box mb={2} flex={1}>
          <AutoField
            field={{
              type: ActionType.checkboxToggle,
              disabled: !modifyFieldObj.value?.source,
              id: 'modify.mandateChange',
              label: 'Change of mandate',
            }}
          />
          {modifyFieldObj.value?.mandateChange ? (
            <FormReadOnly readOnly={!modifyFieldObj.value?.mandateChange}>
              <MandatesWrapper
                showCurrentMandate
                disabledReason={!sourcePot ? 'No source plan selected' : undefined}
                planProviderOptions={[]}
                formikKeyPrefix="modify"
                sourcePot={sourcePot}
              />
              {}
              <Box pb={1} />
            </FormReadOnly>
          ) : null}
          {modelChange ? (
            <BuilderPlanFeesField
              formikKeyPrefixWithoutFee="modify"
              inModify
              hasSubplan={!sourcePot?.parent?.id}
              requiresMandate={false}
              mandateId={
                typeof modifyFieldObj.value?.model === 'string'
                  ? modifyFieldObj.value?.model
                  : modifyFieldObj.value?.model?.id
              }
              productId={sourcePot?.productName?.id}
              providerId={sourcePot?.productProvider?.id}
              pot={sourcePot}
            />
          ) : null}
          <AutoField
            field={{
              type: ActionType.checkboxToggle,
              id: 'modify.feeChange',
              label: 'Change of fee',
            }}
          />
          <AutoField
            field={{
              type: ActionType.checkboxToggle,
              id: 'modify.regularContributionChange',
              label: 'Change of regular contribution',
            }}
          />
          <AutoField
            field={{
              type: ActionType.checkboxToggle,
              id: 'modify.regularWithdrawalChange',
              label: 'Change of regular withdrawal',
            }}
          />
          <AutoField
            field={{
              type: ActionType.checkboxToggle,
              id: 'modify.changeOfOwnership',
              label: 'Change of ownership',
            }}
          />
        </Box>
      </Box>

      <AutoField
        field={{
          type: ActionType.text,
          multiline: true,
          minRows: 14,
          fullWidth: true,
          id: 'modify.note',
          label: 'Modify notes',
        }}
      />
    </Box>
  );
};
